import useScrollBarPosition from '@/shared/lib/hooks/useScrollBarPosition';
import type { RecentPatientType } from '@/shared/lib/hooks/useToken';
import useToken from '@/shared/lib/hooks/useToken';
import { getPatientIdFromPath, NAVBAR_HEIGHT } from '@/shared/lib/util/constants';
import { isProd } from '@/shared/lib/util/environment';
import type { MenuSectionType, SideMenuContextProps } from '@/shared/model';
import { MainMenuKeys, SideMenuContext, useAppFrameStore, usePatientNavBarStore } from '@/shared/model';
import { useAccountStore } from '@/shared/model/AccountStore';
import { useDockedPanelStore } from '@/shared/model/DockedPanelStore';
import { useRecentPagesStore } from '@/shared/model/RecentPagesStore';
import { useSidebarStore } from '@/shared/model/SidebarStore';
import { useSoapPanelStore } from '@/shared/model/SoapPanelStore';
import Announcement from '@/shared/ui/components/Announcements';
import { SIDE_NAV_EXPANDED_WIDTH, SIDE_NAV_RAIL_WIDTH } from '@/shared/ui/components/SideMenus/BaseSideMenuSideNav';
import { recentPatientItems, resolveMenuSections } from '@/shared/ui/components/SideMenus/menuCatalog';
import useMenuContext from '@/shared/ui/components/SideMenus/useMenuContext';
import type { StackProps } from '@mui/material';
import { Box, Stack, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ENVIRONMENT_BAR_HEIGHT } from './EnvironmentBar';
import Navbar from './Navbar';
import NavigationSidebarMenu from './NavigationSidebarMenu';
import NewReleaseBanner from './NewReleaseBanner';
import VersionFooter, { VERSION_FOOTER_HEIGHT, VERSION_FOOTER_Z_INDEX } from './VersionFooter';

type ContentDivProps = StackProps & {
  goTop: boolean;
  setGoTop: (value: boolean) => void;
  setContentScrollPosition: (value: number) => void;
  isStandAloneWindow: boolean;
  bottomGutter?: number;
  /**
   * How much of the content column's horizontal padding the patient bar may cancel, in theme
   * spacing units.  Not the full padding -- see the note where this is computed.
   */
  reclaimableGutter: number;
};
const ContentDiv = ({
  goTop,
  setContentScrollPosition,
  setGoTop,
  children,
  isStandAloneWindow,
  bottomGutter = 0,
  reclaimableGutter,
  ...props
}: ContentDivProps) => {
  const { ref, top } = useScrollBarPosition();

  const setPatientNavSlotEl = usePatientNavBarStore(state => state.setSlotEl);

  useEffect(() => {
    if (top >= 0) {
      setContentScrollPosition(+top);
    }
  }, [setContentScrollPosition, top]);

  useEffect(() => {
    if (goTop) {
      setGoTop(false);
      ref.current?.scrollTo({ top: 0 });
    }
  }, [goTop, ref, setGoTop]);

  return (
    <Stack ref={ref} {...props}>
      {/*
        The patient link bar portals in here.  This is the top of the content column, level with
        the top of the side nav menu, and above the breadcrumbs -- which is why the trail now
        reads below the bar rather than at the very top of the page.  Sticky so the bar survives
        scrolling, and positioned so an unpinned bar can overlay against it without reflowing the
        page.  Empty and zero-height on every non-patient page.
      */}
      {!isStandAloneWindow && (
        <Box
          ref={setPatientNavSlotEl}
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: theme => theme.zIndex.drawer - 1,
            flexShrink: 0,
            // Reclaim the plain gutter either side so the link row spans the full width it has
            // to work with -- it carries far more items than a page body and needs every pixel.
            marginLeft: -reclaimableGutter,
            marginRight: -reclaimableGutter
          }}
        />
      )}
      <Stack sx={{ flex: '1 0 auto', paddingBottom: bottomGutter }}>{children}</Stack>
    </Stack>
  );
};

type Props = {
  children: ReactNode;
  /** Passed to the header search: Enter on a question. */
  onAskQuestion?: (question: string) => void;
};
const AppFrame = ({ children, onAskQuestion }: Props) => {
  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down('lg'));

  // Open state while the nav is pinned.  Unpinned, the nav is driven by hover intent instead.
  const [pinnedOpen, setPinnedOpen] = useState(true);
  const [hoverExpanded, setHoverExpanded] = useState(false);
  const isPinned = useSidebarStore(state => state.isPinned);
  const togglePinned = useSidebarStore(state => state.togglePinned);
  const isAutoHideEnabled = useSidebarStore(state => state.isAutoHideEnabled);
  const toggleAutoHide = useSidebarStore(state => state.toggleAutoHide);
  const [reloadAccounts, setReloadAccounts] = useState<Date | null>(null);

  // What the nav is showing right now: labels visible, either pinned-open or hover-expanded.
  const drawerOpen = isPinned ? pinnedOpen : hoverExpanded;
  // Only a pinned, open nav reserves layout width.  A hover overlay -- and the mobile/tablet
  // temporary drawer -- must never reflow the page.
  const navReservesWidth = !isMobileOrTablet && isPinned && pinnedOpen;

  // Callers (routes, the navbar button) just want "show / hide the nav"; route it to whichever
  // piece of state is in charge for the current mode.
  const setDrawerOpen = useCallback((value: boolean) => {
    if (useSidebarStore.getState().isPinned) {
      setPinnedOpen(value);
    } else {
      setHoverExpanded(value);
    }
  }, []);

  const drawerToggle = useCallback(() => {
    setDrawerOpen(!drawerOpen);
  }, [drawerOpen, setDrawerOpen]);

  // Unpinning parks the nav on the rail immediately; pinning restores a full-width nav.
  const handleTogglePinned = useCallback(() => {
    const willBePinned = !useSidebarStore.getState().isPinned;
    togglePinned();
    setHoverExpanded(false);
    if (willBePinned) {
      setPinnedOpen(true);
    }
  }, [togglePinned]);

  // Mobile/tablet default to a closed overlay drawer; a pinned desktop nav opens by default.
  // Re-syncs whenever the viewport crosses the mobile/tablet <-> desktop boundary.
  //
  // Routed by pin state rather than through `setDrawerOpen`: unpinned, that would land on
  // `hoverExpanded` and float the menu open over the page on every load, with nothing to close
  // it until the pointer had entered and left it.  An unpinned nav starts on its rail.
  useEffect(() => {
    if (useSidebarStore.getState().isPinned) {
      setPinnedOpen(!isMobileOrTablet);
    } else {
      setHoverExpanded(false);
    }
  }, [isMobileOrTablet]);

  const [content, setContent] = useState<ReactNode>(null);
  // One source of truth: the shell reserves exactly what the open nav occupies.
  const drawerWidth = SIDE_NAV_EXPANDED_WIDTH;

  const { setContentScrollPosition, goTop, setGoTop, setMainMenuItems } = useAppFrameStore();
  // Width reserved on the right for a docked side panel (e.g. SOAP Notes). box-sizing:
  // border-box means this padding shrinks the shell into 100vw − dockedWidth, so the
  // fixed panel fills the freed strip instead of overlaying the page.
  const dockedWidth = useDockedPanelStore(state => state.dockedWidth);
  const setIsDockSuppressed = useDockedPanelStore(state => state.setIsDockSuppressed);
  // Intent, not reservation: a collapsed panel reserves no width but is still docked.
  const isDocked = useDockedPanelStore(state => state.isDocked);
  const isDockCollapsed = useDockedPanelStore(state => state.isDockCollapsed);
  const soapSession = useSoapPanelStore(state => state.session);

  const { pathname } = useLocation();
  const recordPage = useRecentPagesStore(state => state.recordPage);

  // Every landing is recorded so the site map can offer the way back.  The site map itself is
  // skipped -- offering "return to the page you are on" is noise.
  useEffect(() => {
    if (!pathname.startsWith('/sitemap')) recordPage(pathname);
  }, [pathname, recordPage]);
  const panelWantsSpace = !!soapSession && isDocked && !isDockCollapsed;

  // Route-driven open/close only ever touches the pinned state — an unpinned nav stays on the
  // rail until the user hovers it, whatever page they land on.
  useEffect(() => {
    // Leaving the patient pages closes the alert review panel. Left open, it came back on the next
    // patient opened -- from the patients list, say -- as though an alert had been asked for.
    if (!getPatientIdFromPath(pathname) && useAccountStore.getState().showAlertReviewFollowupWindow) {
      useAccountStore.getState().setShowAlertReviewFollowupWindow(false);
      useAccountStore.getState().setAlertReviewFollowupData([]);
    }

    if (pathname.startsWith('/home')) {
      setPinnedOpen(false);
      return;
    }

    if (getPatientIdFromPath(pathname) && useSoapPanelStore.getState().session && useDockedPanelStore.getState().isDocked) {
      setPinnedOpen(false);
      useDockedPanelStore.getState().setIsDockCollapsed(false);
      return;
    }

    setPinnedOpen(true);
  }, [pathname]);

  // Edge-triggered so a manual nav toggle is never undone on the next render.
  const wantedSpaceRef = useRef(false);
  useEffect(() => {
    const isTakingSpace = panelWantsSpace && !wantedSpaceRef.current;
    const isReleasingSpace = !panelWantsSpace && wantedSpaceRef.current;
    wantedSpaceRef.current = panelWantsSpace;

    if (isTakingSpace) {
      setPinnedOpen(false);
      setIsDockSuppressed(false);
      return;
    }
    // Closing or collapsing the panel gives the nav bar back.
    if (isReleasingSpace) {
      setPinnedOpen(true);
      return;
    }

    setIsDockSuppressed(panelWantsSpace && navReservesWidth);
  }, [panelWantsSpace, navReservesWidth, setIsDockSuppressed]);

  // The shell only gives up room for a panel that is actually showing.
  const reservedDockWidth = navReservesWidth ? 0 : dockedWidth;

  const { getRecentPatients } = useToken();
  // Role and enabled services, in the shape the menu catalog reads.
  const menuContext = useMenuContext();
  const currentUser = menuContext.currentUser;
  const [recentPatients, setRecentPatients] = useState<RecentPatientType[]>([]);

  const [pathName, setPathName] = useState('');

  const isStandAloneWindow = pathName === '/copilot';

  const [topBannerHeight, setTopBannerHeight] = useState(0);
  const topBannerObserverRef = useRef<ResizeObserver | null>(null);
  const topBannerRef = useCallback((node: HTMLDivElement | null) => {
    topBannerObserverRef.current?.disconnect();
    topBannerObserverRef.current = null;

    if (!node) {
      setTopBannerHeight(0);
      document.documentElement.style.setProperty('--top-banner-height', '0px');
      return;
    }

    const observer = new ResizeObserver(([entry]) => {
      setTopBannerHeight(entry.contentRect.height);
      document.documentElement.style.setProperty('--top-banner-height', `${entry.contentRect.height}px`);
    });
    observer.observe(node);
    topBannerObserverRef.current = observer;
  }, []);

  const envBarClearance = topBannerHeight > 0 && !isProd() ? ENVIRONMENT_BAR_HEIGHT : 0;

  // Horizontal gutters of the content column, kept as spacing numbers rather than inlined into
  // the padding shorthand so the patient link bar can cancel them and run the column's full width.
  const contentGutterTop = isStandAloneWindow ? 0 : isMobileOrTablet ? 2 : 0;
  const contentGutterRight = isStandAloneWindow ? 0 : isMobileOrTablet ? 2 : 3;
  const contentGutterLeft = isStandAloneWindow ? 0 : isMobileOrTablet ? 2 : navReservesWidth ? 3 : 12;
  // Only the plain-gutter share of that padding is the patient bar's to take.  With the nav on
  // its rail the left indent is 12 because ~72px of the row is still the rail; just 3 of it is
  // visible gutter.  Cancelling the whole 12 slid the bar under the rail.
  const contentReclaimableGutter = isStandAloneWindow ? 0 : isMobileOrTablet ? 2 : 3;

  const providerValue: SideMenuContextProps = useMemo(
    () => ({
      drawerOpen,
      setDrawerOpen,
      drawerToggle,
      isPinned,
      togglePinned: handleTogglePinned,
      isAutoHideEnabled,
      toggleAutoHide,
      setHoverExpanded,
      setContent,
      content,
      reloadAccounts,
      setReloadAccounts,
      isMobileOrTablet
    }),
    [
      drawerOpen,
      setDrawerOpen,
      drawerToggle,
      isPinned,
      handleTogglePinned,
      isAutoHideEnabled,
      toggleAutoHide,
      setContent,
      content,
      reloadAccounts,
      setReloadAccounts,
      isMobileOrTablet
    ]
  );

  useEffect(() => {
    setMainMenuItems(resolveMenuSections(menuContext));
  }, [menuContext, setMainMenuItems]);

  useEffect(() => {
    const patients = getRecentPatients();
    setRecentPatients(patients);
    setPathName(window.location.pathname.slice(window.location.pathname.lastIndexOf('/'), window.location.pathname.length));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [window.location.pathname]);

  useEffect(() => {
    useAppFrameStore.setState(prev => ({
      mainMenuItems: new Map<MainMenuKeys, MenuSectionType>(prev.mainMenuItems).set(MainMenuKeys.RecentPatients, {
        title: 'Recent Patients',
        options: recentPatientItems(recentPatients, currentUser)
      })
    }));
    //need this so we can keep recent patients up-to-date

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recentPatients, currentUser]);

  return (
    <SideMenuContext.Provider value={providerValue}>
      <Stack
        sx={{
          width: '100vw',
          height: '100%',
          position: 'relative',
          // No transition: padding-right is a layout property, so animating it re-laid out the
          // whole page on every frame for 150ms -- at the same moment a docking panel was gliding
          // in beside it. The shell takes its new width in one pass instead, and the panel's own
          // slide covers the change.
          paddingRight: reservedDockWidth ? `${reservedDockWidth}px` : 0
        }}
      >
        {/* Always mounted so its measured height drops back to 0 when both banners are gone. */}
        <Box ref={topBannerRef} sx={{ position: 'relative', zIndex: theme.zIndex.drawer + 2 }}>
          <NewReleaseBanner />
          {currentUser?.relyingParty?.isMock && (
            <Box
              sx={{
                backgroundColor: 'warning.main',
                color: 'warning.contrastText',
                textAlign: 'center',
                py: 0.5,
                px: 2,
                fontSize: 13,
                fontWeight: 600
              }}
            >
              ⚠️ You are working in “{currentUser?.relyingParty?.name}” — a MOCK / TEST pharmacy. Its data is excluded from reporting and
              analytics.
            </Box>
          )}
        </Box>
        <Announcement />
        {!isStandAloneWindow && (
          <Navbar
            elevation={0}
            enableColorOnDark
            color="inherit"
            onAskQuestion={onAskQuestion}
            sx={{ height: NAVBAR_HEIGHT, marginTop: envBarClearance, zIndex: 11 }}
          />
        )}
        <Stack direction={'row'} sx={{ width: '100%', flex: 1, minHeight: 0, overflow: 'hidden' }}>
          {!isStandAloneWindow && (
            <NavigationSidebarMenu
              topOffset={topBannerHeight + envBarClearance}
              sx={{ width: isMobileOrTablet ? 0 : navReservesWidth ? drawerWidth : 'auto' }}
            />
          )}
          <ContentDiv
            {...{ goTop, setContentScrollPosition, setGoTop }}
            bottomGutter={isStandAloneWindow ? 0 : isMobileOrTablet ? 2 : 8}
            reclaimableGutter={contentReclaimableGutter}
            sx={{
              // Do NOT shrink the collapsed-nav left indent (12). ContentDiv is 100% wide while
              // the collapsed rail still occupies ~77px of the same row, so this padding is what
              // keeps the breadcrumbs and patient card clear of the rail rather than under it --
              // 12 leaves the same ~3 of visible gutter the right side gets once the rail is
              // subtracted. With the nav pinned open the drawer already reserves its own width,
              // so the left indent is a plain gutter and matches the right at 3.
              // On mobile/tablet the rail is an overlay drawer instead, so it takes no row space.
              padding: theme => theme.spacing(contentGutterTop, contentGutterRight, 0, contentGutterLeft),
              width: `calc(100% - ${navReservesWidth ? drawerWidth : 0}px)`,
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              overflowX: 'hidden',
              WebkitOverflowScrolling: 'touch'
            }}
            isStandAloneWindow={isStandAloneWindow}
          >
            {children}
          </ContentDiv>
        </Stack>

        {!isMobileOrTablet && (
          <VersionFooter
            sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: VERSION_FOOTER_HEIGHT, zIndex: VERSION_FOOTER_Z_INDEX }}
            contentOffset={!isStandAloneWindow ? (navReservesWidth ? drawerWidth : 0) : 0}
            // An unpinned nav reserves no width but still covers the rail, so the badge clears that
            // instead of starting underneath it.
            badgeOffset={!isStandAloneWindow ? (navReservesWidth ? drawerWidth : SIDE_NAV_RAIL_WIDTH) : 0}
          />
        )}
      </Stack>
    </SideMenuContext.Provider>
  );
};

export default AppFrame;
